import { FC, FunctionComponent, memo, useRef } from "react"
import { emptyQuestionSpec, QuestionProps } from "./ChecklistQuestionTypes"
import { ChecklistTextFooter, ChecklistTextQuestion } from "./ChecklistTextQuestion"
import { Select } from "@nova/component-select"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ChecklistRadioFooter, ChecklistRadioQuestion } from "./ChecklistRadioQuestion"
import { ChecklistHeaderFooter, ChecklistHeaderQuestion } from "./ChecklistHeaderQuestion"
import { useDrag, useDragLayer, useDrop } from "react-dnd"
import { QuestionType } from "@nova/schemas"
import classNames from "classnames"
import {
    ChecklistMultiSelectFooter,
    ChecklistMultiSelectQuestion,
} from "./ChecklistMultiSelectQuestion"
import {
    ChecklistUserSelectFooter,
    ChecklistUserSelectQuestion,
} from "./ChecklistUserSelectQuestion"
import {
    ChecklistAircraftSelectQuestion,
    ChecklistAircraftSelectFooter,
} from "./ChecklistAircraftSelectQuestion"

type QuestionBuilderProps = QuestionProps & { index: number; autoFocus?: boolean }

const QuestionBodyByQuestionType: { [T in QuestionType]: FC<QuestionProps<T>> } = {
    text: ChecklistTextQuestion,
    "multi-select": ChecklistMultiSelectQuestion,
    radio: ChecklistRadioQuestion,
    "user-select": ChecklistUserSelectQuestion,
    "aircraft-select": ChecklistAircraftSelectQuestion,
    header: ChecklistHeaderQuestion,
}
const QuestionFooterByQuestionType: { [T in QuestionType]: FC<QuestionProps<T>> } = {
    text: ChecklistTextFooter,
    "multi-select": ChecklistMultiSelectFooter,
    radio: ChecklistRadioFooter,
    "user-select": ChecklistUserSelectFooter,
    "aircraft-select": ChecklistAircraftSelectFooter,
    header: ChecklistHeaderFooter,
}

export const QuestionBuilder: FunctionComponent<QuestionBuilderProps> = memo(
    ({ spec, questionControls, index, autoFocus = false }) => {
        const ref = useRef<HTMLDivElement>(null)
        const dragHandleRef = useRef<HTMLDivElement>(null)

        const [, drop] = useDrop({
            accept: "QUESTION",
            hover: (item: { index: number; id: string }, monitor) => {
                if (!ref.current) return
                const dragIndex = item.index
                const hoverIndex = index
                if (dragIndex === hoverIndex) return

                const hoverBoundingRect = ref.current.getBoundingClientRect()
                const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
                const clientOffset = monitor.getClientOffset()
                const hoverClientY = (clientOffset?.y ?? 0) - hoverBoundingRect.top

                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
                if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

                const direction = dragIndex < hoverIndex ? 1 : -1
                if (Math.abs(dragIndex - hoverIndex) !== 1) return

                questionControls.moveQuestion(item.id, {
                    direction,
                })
                item.index = dragIndex + direction
            },
        })

        const [, drag, preview] = useDrag({
            type: "QUESTION",
            item: { id: spec.id, index },
        })

        const { isDragging, isAnythingDragging } = useDragLayer((monitor) => ({
            isDragging: monitor.isDragging() && monitor.getItem()?.id === spec.id,
            isAnythingDragging: monitor.isDragging(),
        }))

        drag(dragHandleRef)
        drop(preview(ref))

        const QuestionBody = QuestionBodyByQuestionType[spec.type] as FC<
            QuestionProps<typeof spec.type>
        >
        const QuestionFooter = QuestionFooterByQuestionType[spec.type] as FC<
            QuestionProps<typeof spec.type>
        >

        return (
            <div
                ref={ref}
                className={classNames(
                    "w-full flex justify-center group transition-opacity select-none max-w-xl relative",
                    {
                        "opacity-30": isDragging,
                    }
                )}
                style={{ viewTransitionName: `question-${spec.id}` }}
            >
                <div className="absolute flex flex-col -left-8 opacity-0 group-hover:opacity-100 items-center shrink-0">
                    <IconButton
                        onClick={() => questionControls.moveQuestion(spec.id, { direction: -1 })}
                        disabled={index === 0}
                        className="text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowDropUp} />
                    </IconButton>

                    <div
                        ref={dragHandleRef}
                        data-drag-type="touch"
                        className="cursor-grab active:cursor-grabbing text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.DragHandle} />
                    </div>

                    <IconButton
                        onClick={() => questionControls.moveQuestion(spec.id, { direction: 1 })}
                        disabled={questionControls.getNumQuestions() === index + 1}
                        className="text-gray-100"
                    >
                        <Icons.SmallIcon Icon={Icons.ArrowDropdown} />
                    </IconButton>
                </div>
                <div
                    className={classNames(
                        "flex-1 divide-y divide-white-100 rounded-xl border border-white-100",
                        {
                            "group-hover:ring-2 group-hover:ring-white-300 focus-within:ring-2 focus-within:ring-white-300":
                                !isDragging && !isAnythingDragging,
                            "ring-2 ring-blue-500": isDragging,
                        }
                    )}
                >
                    <div className="p-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <div>Type*</div>
                            <div className="w-fit">
                                <Select<QuestionType>
                                    onChange={(v) =>
                                        v !== spec.type &&
                                        questionControls.editQuestion(spec.id, emptyQuestionSpec(v))
                                    }
                                    options={[
                                        { label: "Text Input", value: "text" },
                                        { label: "Multiple Answers", value: "multi-select" },
                                        { label: "Multiple Choice", value: "radio" },
                                        { label: "User Select", value: "user-select" },
                                        { label: "Aircraft Select", value: "aircraft-select" },
                                        { label: "Header", value: "header" },
                                    ]}
                                    value={spec.type}
                                />
                            </div>
                        </div>
                        <QuestionBody
                            spec={spec}
                            questionControls={questionControls}
                            autoFocus={autoFocus}
                        />
                    </div>
                    <QuestionFooter spec={spec} questionControls={questionControls} />
                </div>
            </div>
        )
    }
)

QuestionBuilder.displayName = "QuestionBuilder"
